import React, { useCallback, useMemo, useState } from 'react';
import { Box, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from '../../api';
import BrandKitStep from './steps/BrandKitStep';
import GuidanceStep from './steps/GuidanceStep';
import PreviewStep from './steps/PreviewStep';
import ScriptEditor from './steps/ScriptEditor';
import StoryboardEditor from './steps/StoryboardEditor';
import StyleStep from './steps/StyleStep';
import { vb } from './theme';
import { LessonVideoStatus, VideoMode, VideoProject, isTerminalJob } from './types';
import { apiMessage } from './useAutosave';

type Step = 'style' | 'brand' | 'guidance' | 'editor' | 'preview';

const hasContent = (p: VideoProject | null): boolean =>
  Boolean(p && (p.mode === 'presenter' ? p.sections.length : p.scenes.length));

// Derive the resume step from persisted project state (§6): nothing lives
// only in transient React state.
export function initialStep(project: VideoProject | null, activeJobPending: boolean): Step {
  if (!project) return 'style';
  if (activeJobPending) return 'preview';
  if (!hasContent(project)) return 'guidance';
  return 'editor';
}

// The whole "Generate video" flow (prototype screens 2 → 5) on top of the
// lesson editor. Server state is the project; this only holds the step.
const VideoBuilderDialog: React.FC<{
  open: boolean;
  programId: string;
  moduleId: string;
  lessonId: string;
  lessonName: string;
  moduleName: string;
  status: LessonVideoStatus;
  resumeProject: VideoProject | null;
  startAt?: Step;
  onClose: () => void;
  onChanged: () => void;
}> = ({
  open,
  programId,
  moduleId,
  lessonId,
  lessonName,
  moduleName,
  status,
  resumeProject,
  startAt,
  onClose,
  onChanged
}) => {
  const [project, setProject] = useState<VideoProject | null>(resumeProject);
  const [step, setStep] = useState<Step>(
    () => startAt ?? initialStep(resumeProject, Boolean(status.activeJob && !isTerminalJob(status.activeJob.status)))
  );
  const [busy, setBusy] = useState(false);
  const onProject = useCallback((p: VideoProject) => setProject(p), []);
  const isFirstTimeKit = useMemo(
    () =>
      !status.brandKit ||
      Object.values(status.brandKit).every(
        (v, i) =>
          v === Object.values({ background: '#FFFFFF', text: '#1C1C1C', outline: '#1C1C1C', fill: '#F79420' })[i]
      ),
    [status.brandKit]
  );

  const chooseMode = async (mode: VideoMode) => {
    try {
      setBusy(true);
      const res: any = await API.createVideoProject({
        programId,
        moduleId,
        lessonId,
        mode,
        targetDurationSeconds: 90
      });
      setProject(res.data);
      setStep(mode === 'explainer' ? 'brand' : 'guidance');
      onChanged();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not start the video'));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onChanged();
    onClose();
  };

  const wide = step === 'editor' || step === 'preview';
  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth={wide ? 'lg' : 'md'}
      PaperProps={{
        sx: {
          background: vb.surface,
          border: `1px solid ${vb.border}`,
          borderRadius: '14px',
          p: 3,
          color: vb.text,
          height: wide ? 'min(92vh, 980px)' : 'auto',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      data-testid="video-builder-dialog"
    >
      <IconButton
        aria-label="Close"
        onClick={close}
        sx={{ position: 'absolute', top: 10, right: 10, color: vb.textTertiary }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {step === 'style' && (
          <StyleStep
            lessonName={lessonName}
            busy={busy}
            onContinue={(m) => {
              chooseMode(m);
            }}
          />
        )}
        {step === 'brand' && project && (
          <BrandKitStep
            project={project}
            orgKit={status.brandKit}
            isFirstTime={isFirstTimeKit}
            onBack={() => setStep('style')}
            onContinue={(p) => {
              setProject(p);
              setStep('guidance');
            }}
          />
        )}
        {step === 'guidance' && project && (
          <GuidanceStep
            project={project}
            onBack={() => setStep(project.mode === 'explainer' ? 'brand' : 'style')}
            onDrafted={(p) => {
              setProject(p);
              setStep('editor');
              onChanged();
            }}
          />
        )}
        {step === 'editor' && project && project.mode === 'presenter' && (
          <ScriptEditor
            project={project}
            lessonName={lessonName}
            onProject={onProject}
            onPreview={() => setStep('preview')}
            onBackToGuidance={() => setStep('guidance')}
          />
        )}
        {step === 'editor' && project && project.mode === 'explainer' && (
          <StoryboardEditor
            project={project}
            lessonName={lessonName}
            moduleName={moduleName}
            onProject={onProject}
            onPreview={() => setStep('preview')}
            onBackToGuidance={() => setStep('guidance')}
          />
        )}
        {step === 'preview' && project && (
          <PreviewStep
            project={project}
            lessonName={lessonName}
            allowance={status.allowance}
            onProject={onProject}
            onKeepEditing={() => setStep('editor')}
            onBackground={close}
            onSaved={close}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default VideoBuilderDialog;
